import { Canvas as FabricCanvas, FabricObject } from 'fabric';
import { useEffect } from 'react';

// GAPI-61780: the rotate handle snaps to every multiple of this angle (0, 45, 90…)
// when the element comes within the threshold of it. Holding Ctrl (Windows) or
// Cmd (Mac) rotates freely, the same bypass Canva uses for its snapping.
export const ROTATION_SNAP_ANGLE = 45;
export const ROTATION_SNAP_THRESHOLD = 10;

type ModifierState = { ctrlKey?: boolean; metaKey?: boolean } | undefined;

function applyRotationSnap(target: FabricObject, e: ModifierState) {
  const free = !!(e?.ctrlKey || e?.metaKey);
  target.snapAngle = free ? undefined : ROTATION_SNAP_ANGLE;
  target.snapThreshold = free ? undefined : ROTATION_SNAP_THRESHOLD;
}

/**
 * Fabric's rotate handler (`controlsUtils.rotationWithSnapping`) already does the
 * snapping from the target's `snapAngle` / `snapThreshold`, and every rotatable
 * object and the multi-selection use it. So instead of threading the values
 * through each element type, they are stamped on whatever is being transformed:
 * once when the drag starts, and again before every pointer move so pressing or
 * releasing Ctrl/Cmd mid-drag takes effect immediately.
 */
export function attachRotationSnap(canvas: FabricCanvas) {
  const disposeStart = canvas.on('before:transform', ({ e, transform }) => {
    applyRotationSnap(transform.target, e as ModifierState);
  });
  const disposeMove = canvas.on('mouse:move:before', ({ e }) => {
    const target = canvas._currentTransform?.target;
    if (target) {
      applyRotationSnap(target, e as ModifierState);
    }
  });

  return () => {
    disposeStart();
    disposeMove();
  };
}

function useRotationSnap(fabricCanvas: FabricCanvas | undefined) {
  useEffect(() => (fabricCanvas ? attachRotationSnap(fabricCanvas) : undefined), [fabricCanvas]);
}

export default useRotationSnap;
